/**
 * Home page component
 * Landing page for the SoiledIt application
 * 
 * @module pages/Home
 * @requires react
 * @requires react-router-dom
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import { Helmet } from 'react-helmet';

/**
 * Home component - Landing page
 * @returns {JSX.Element} Home page component
 */
const Home = () => {
  // ── SEO constants for the Home page ─────────────────────────────
  const ABSOLUTE_BASE = 'https://www.soiledit.com';
  const title = 'Perfect Soil for Your Perfect Plants | SoiledIt';
  const description =
    'Custom, science-backed soil mixes tailored to each plant’s needs. Shop herb, succulent, and houseplant blends with fast, eco-friendly delivery.';
  const canonical = `${ABSOLUTE_BASE}/`;
  const ogImage = `${ABSOLUTE_BASE}/images/og/soiledit-home.jpg`; // make sure file exists

  // Optional: JSON-LD (helps Google understand your site)
  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: ABSOLUTE_BASE,
    name: 'SoiledIt',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${ABSOLUTE_BASE}/plants?query={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'SoiledIt',
    url: ABSOLUTE_BASE,
    logo: `${ABSOLUTE_BASE}/images/logo.png`,
    sameAs: [] // add social URLs when you have them
  };

  return (
    <div className="home-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="SoiledIt" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(websiteLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(orgLd)}
        </script>
      </Helmet>
      <main id="main" role="main">
      <section className="hero">
        <div className="hero-content">
          <h1>Perfect Soil for Your Perfect Plants</h1>
          <p>Custom soil mixes tailored to your plants' specific needs</p>
          <div className="hero-buttons">
            <Link to="/plants" className="btn btn-primary">Find Your Mix</Link>
            <Link to="/products" className="btn btn-secondary">Browse Products</Link>
          </div>
        </div>
      </section>
      
      <section className="features">
        <h2>Why Choose SoiledIt?</h2>
        
        <div className="feature-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-seedling" aria-hidden="true"></i>
            </div>
            <h3>Plant-Specific Mixes</h3>
            <p>Every plant has unique soil needs. Our custom mixes are formulated specifically for your plants.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-flask" aria-hidden="true"></i>
            </div>
            <h3>Science-Backed Formulas</h3>
            <p>Our soil experts use the latest research to create mixes with optimal pH, nutrients, and drainage.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-leaf" aria-hidden="true"></i>
            </div>
            <h3>Organic Ingredients</h3>
            <p>We use sustainable, organic components to ensure your plants thrive without harmful chemicals.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-truck" aria-hidden="true"></i>
            </div>
            <h3>Doorstep Delivery</h3>
            <p>Fast, reliable shipping right to your door in eco-friendly packaging.</p>
          </div>
        </div>
      </section>
      
      <section className="how-it-works">
        <h2>How It Works</h2>
        
        <ol className="steps">
          <li className="step">
            <div className="step-number">1</div>
            <h3>Search Your Plant</h3>
            <p>Enter your plant's name in our database to find its specific soil requirements.</p>
          </li>
          
          <li className="step">
            <div className="step-number">2</div>
            <h3>Get Recommendations</h3>
            <p>We'll suggest the perfect soil mix based on your plant's needs.</p>
          </li>
          
          <li className="step">
            <div className="step-number">3</div>
            <h3>Place Your Order</h3>
            <p>Choose your soil quantity and place your order with secure checkout.</p>
          </li>
          
          <li className="step">
            <div className="step-number">4</div>
            <h3>Watch Your Plants Thrive</h3>
            <p>Enjoy healthier, happier plants with soil that meets their exact needs.</p>
          </li>
        </ol>
        
        <div className="cta">
          <Link to="/plants" className="btn btn-primary">Get Started Now</Link>
        </div>
      </section>
      
      {/* <section className="testimonials">
        <h2>What Our Customers Say</h2>
        
        <div className="testimonial-grid">
          <div className="testimonial-card">
            <p className="testimonial-text">"My fiddle leaf fig has never looked better since I started using SoiledIt's custom mix. New leaves every month!"</p>
            <p className="testimonial-author">- Sarah K.</p>
          </div>
          
          <div className="testimonial-card">
            <p className="testimonial-text">"As a plant collector with over 100 varieties, SoiledIt has made caring for each plant's soil needs so much easier."</p>
            <p className="testimonial-author">- Michael T.</p>
          </div>
          
          <div className="testimonial-card">
            <p className="testimonial-text">"My orchids bloomed twice this year after switching to SoiledIt's specialty orchid mix. I'm impressed!"</p>
            <p className="testimonial-author">- Jennifer L.</p>
          </div>
        </div>
      </section> */}
      </main>
    </div>
  );
};

export default Home;